import {useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";

import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import {
	AREA_OPTIONS,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {getBirthYears as getBirthYearsFn} from "@/utils/functions";
import {useStaffs} from "@/utils/hooks";

import "./index.scss";

// ── Role badge helpers ────────────────────────────────────────────────────────

const ROLE_BADGE = {
	Portiere: {short: "POR", cls: "gk"},
	Difensore: {short: "DIF", cls: "def"},
	Mediano: {short: "MED", cls: "dmid"},
	Centrocampista: {short: "CEN", cls: "mid"},
	Trequartista: {short: "TRE", cls: "amid"},
	Attaccante: {short: "ATT", cls: "att"},
};

const ROLE_ORDER = {
	Portiere: 1,
	Difensore: 2,
	Mediano: 3,
	Centrocampista: 4,
	Trequartista: 5,
	Attaccante: 6,
};

const getRoleBadge = (player) => {
	if (player.goalkeeper) return ROLE_BADGE.Portiere;
	return ROLE_BADGE[player.primaryRole] ?? {short: "---", cls: "default"};
};

const getRoleOrder = (player) =>
	player.goalkeeper ? 1 : ROLE_ORDER[player.primaryRole] ?? 99;

const sortByRole = (arr) =>
	[...arr].sort((a, b) => {
		const ro = getRoleOrder(a) - getRoleOrder(b);
		return ro !== 0 ? ro : a.lastName.localeCompare(b.lastName);
	});

const sortByName = (arr) =>
	[...arr].sort((a, b) => a.lastName.localeCompare(b.lastName));

// ── Player row ────────────────────────────────────────────────────────────────

function PlayerRow({player, selected, onToggle, saving}) {
	const badge = getRoleBadge(player);
	const year = player.dateOfBirth
		? moment(player.dateOfBirth).format("YYYY")
		: "—";

	return (
		<div
			className={
				"rs-row" +
				(selected ? " rs-row--in" : "") +
				(saving ? " rs-row--saving" : "")
			}
			onClick={() => !saving && onToggle(player)}
		>
			<span className={"rs-role-badge rs-role-badge--" + badge.cls}>
				{badge.short}
			</span>
			<div className="rs-row__info">
				<span className="rs-row__name">
					{player.firstName} {player.lastName}
				</span>
				<span className="rs-row__meta">{year}</span>
			</div>
			<div className={"rs-row__icon" + (selected ? " rs-row__icon--in" : "")}>
				{selected ? (
					<RemoveIcon fontSize="small" />
				) : (
					<AddIcon fontSize="small" />
				)}
			</div>
		</div>
	);
}

// ── Staff row ─────────────────────────────────────────────────────────────────

function StaffRow({staff, selected, onToggle, saving}) {
	return (
		<div
			className={
				"rs-row" +
				(selected ? " rs-row--in" : "") +
				(saving ? " rs-row--saving" : "")
			}
			onClick={() => !saving && onToggle(staff)}
		>
			<span className="rs-role-badge rs-role-badge--default">
				{staff.unit?.substring(0, 3).toUpperCase() ?? "STF"}
			</span>
			<div className="rs-row__info">
				<span className="rs-row__name">
					{staff.firstName} {staff.lastName}
				</span>
				<span className="rs-row__meta">{staff.role ?? "—"}</span>
			</div>
			<div className={"rs-row__icon" + (selected ? " rs-row__icon--in" : "")}>
				{selected ? (
					<RemoveIcon fontSize="small" />
				) : (
					<AddIcon fontSize="small" />
				)}
			</div>
		</div>
	);
}

// ── Section divider ───────────────────────────────────────────────────────────

function SectionHeader({label, count}) {
	return (
		<div className="rs-section-header">
			<span className="rs-section-header__label">{label}</span>
			<span className="rs-section-header__count">{count}</span>
		</div>
	);
}

// ── Main page ─────────────────────────────────────────────────────────────────

function HandleRosterPage(props) {
	const {handleNotification, source, type = "players"} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(false);
	const [selected, setSelected] = useState([]);
	const [savingIds, setSavingIds] = useState(new Set());

	// Filters
	const [search, setSearch] = useState("");
	const [includeExternal, setIncludeExternal] = useState(false);
	const [roleFilter, setRoleFilter] = useState(null);
	const [birthYearFilter, setBirthYearFilter] = useState(null);
	const [unitFilter, setUnitFilter] = useState(null);

	// All available players (loaded once)
	const [allPlayers, setAllPlayers] = useState([]);
	const [rosterName, setRosterName] = useState("");

	const {staffs} = useStaffs({
		setLoading: null,
		handleNotification: undefined,
		chargeData: type === "staffs",
		prospect: source === "manager" ? false : undefined,
	});

	// ── Load all players ──────────────────────────────────────────────────────
	useEffect(() => {
		if (type !== "players") return;
		let current = true;
		const load = async () => {
			setLoading(true);
			try {
				const response = await PlayerAPI.list({
					prospect:
						source === "manager" && !includeExternal ? false : undefined,
					seasonId,
				});
				if (current) setAllPlayers(sortByRole(response || []));
			} catch {
				current &&
					handleNotification &&
					handleNotification(ERROR_OPERATION, "error");
			} finally {
				if (current) setLoading(false);
			}
		};
		load();
		return () => {
			current = false;
		};
	}, [type, id, seasonId, source, includeExternal]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Load roster (current selection) ──────────────────────────────────────
	useEffect(() => {
		const load = async () => {
			setLoading(true);
			try {
				const response = await RosterAPI.getById(id);
				setRosterName(response?.name ?? "");
				if (type === "players") {
					setSelected(response?.players?.map((p) => p.player) ?? []);
				} else {
					setSelected(response?.staff?.map((s) => s.staff) ?? []);
				}
			} catch {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [type, id]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Toggle with immediate save ───────────────────────────────────────────
	const toggle = async (element) => {
		const elementId = element.id;
		const wasSelected = selected.some((e) => e.id === elementId);

		setSavingIds((prev) => new Set(prev).add(elementId));
		setSelected((prev) =>
			wasSelected ? prev.filter((e) => e.id !== elementId) : [...prev, element],
		);

		try {
			if (wasSelected) {
				if (type === "players") {
					await RosterAPI.deletePlayer(id, elementId);
				} else {
					await RosterAPI.deleteStaff(id, elementId);
				}
			} else {
				if (type === "players") {
					await RosterAPI.addPlayers([{rosterId: id, playerId: elementId}]);
				} else {
					await RosterAPI.addStaffs([{rosterId: id, staffId: elementId}]);
				}
			}
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			setSelected((prev) =>
				wasSelected
					? [...prev, element]
					: prev.filter((e) => e.id !== elementId),
			);
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setSavingIds((prev) => {
				const next = new Set(prev);
				next.delete(elementId);
				return next;
			});
		}
	};

	const isSelected = (element) => selected.some((e) => e.id === element.id);

	// ── Client-side filter ────────────────────────────────────────────────────
	const pool = type === "players" ? allPlayers : sortByName(staffs ?? []);

	const filtered = useMemo(() => {
		let items = pool;
		if (search) {
			const q = search.toLowerCase();
			items = items.filter((p) =>
				(p.firstName + " " + p.lastName).toLowerCase().includes(q),
			);
		}
		if (roleFilter && type === "players") {
			if (roleFilter.value === "Goalkeeper") {
				items = items.filter((p) => p.goalkeeper);
			} else {
				items = items.filter(
					(p) => !p.goalkeeper && p.primaryRole === roleFilter.label,
				);
			}
		}
		if (birthYearFilter && type === "players") {
			items = items.filter(
				(p) =>
					p.dateOfBirth &&
					moment(p.dateOfBirth).year() === birthYearFilter.value,
			);
		}
		if (unitFilter && type === "staffs") {
			items = items.filter((s) => s.unit === unitFilter.value);
		}
		return items;
	}, [pool, search, roleFilter, birthYearFilter, unitFilter, type]);

	const inRosa = filtered.filter(isSelected);
	const available = filtered.filter((e) => !isSelected(e));

	// Unique staff units for filter
	const staffUnits = useMemo(() => {
		const units = [
			...new Set((staffs ?? []).map((s) => s.unit).filter(Boolean)),
		];
		return units.map((u) => ({value: u, label: u}));
	}, [staffs]);

	// ── Navigation ────────────────────────────────────────────────────────────
	const goBack = () => {
		navigate(
			source === "manager"
				? "/manager/teams/" + id
				: "/development/rosters/" + id,
		);
	};

	const breadcrumbsElements = [
		{
			label: source === "manager" ? "Rose" : "Roster",
			path: source === "manager" ? "/manager/teams" : "/development/rosters",
		},
		{label: "/"},
		{
			label: rosterName || "—",
			path:
				source === "manager"
					? "/manager/teams/" + id
					: "/development/rosters/" + id,
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
	];

	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<Page className="rs-page">
			<Loading visible={loading} />

			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={
					<div className="d-flex align-items-center gap-2">
						<span className="rs-counter-badge">
							<CheckIcon sx={{fontSize: 13}} />
							{selected.length} {type === "players" ? "giocatori" : "staff"}
						</span>
						<MainActions actions={mainActions} />
					</div>
				}
			/>

			{/* Filters */}
			<div className="rs-filters">
				<div className="rs-filters__field-group">
					<span className="rs-filters__field-label">
						{type === "players" ? "Giocatore" : "Staff"}
					</span>
					<VLFTextField
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Cerca…"
						size="small"
					/>
				</div>
				{type === "players" && source === "manager" && (
					<label>
						<input
							type="checkbox"
							checked={includeExternal}
							onChange={(event) => setIncludeExternal(event.target.checked)}
						/>{" "}
						Includi giocatori esterni
					</label>
				)}
				{type === "players" && (
					<>
						<div className="rs-filters__select">
							<VLFSelectNew
								options={AREA_OPTIONS}
								sorted={false}
								value={roleFilter}
								onChange={setRoleFilter}
								label="Ruolo"
								isClearable
							/>
						</div>
						<div className="rs-filters__select">
							<VLFSelectNew
								options={getBirthYearsFn()}
								sorted={false}
								value={birthYearFilter}
								onChange={setBirthYearFilter}
								label="Anno"
								isClearable
							/>
						</div>
					</>
				)}
				{type === "staffs" && staffUnits.length > 0 && (
					<div className="rs-filters__select">
						<VLFSelectNew
							options={staffUnits}
							sorted={false}
							value={unitFilter}
							onChange={setUnitFilter}
							label="Area"
							isClearable
						/>
					</div>
				)}
			</div>

			{/* List */}
			<div className="rs-list">
				{inRosa.length > 0 && (
					<>
						<SectionHeader label="In rosa" count={inRosa.length} />
						<div className="rs-section-body">
							{inRosa.map((el) =>
								type === "players" ? (
									<PlayerRow
										key={el.id}
										player={el}
										selected
										onToggle={toggle}
										saving={savingIds.has(el.id)}
									/>
								) : (
									<StaffRow
										key={el.id}
										staff={el}
										selected
										onToggle={toggle}
										saving={savingIds.has(el.id)}
									/>
								),
							)}
						</div>
					</>
				)}

				<>
					<SectionHeader label="Disponibili" count={available.length} />
					<div className="rs-section-body">
						{available.length > 0 ? (
							available.map((el) =>
								type === "players" ? (
									<PlayerRow
										key={el.id}
										player={el}
										selected={false}
										onToggle={toggle}
										saving={savingIds.has(el.id)}
									/>
								) : (
									<StaffRow
										key={el.id}
										staff={el}
										selected={false}
										onToggle={toggle}
										saving={savingIds.has(el.id)}
									/>
								),
							)
						) : (
							<div className="rs-empty">
								{search || roleFilter || birthYearFilter || unitFilter
									? "Nessun risultato per i filtri applicati."
									: type === "staffs"
										? "Tutti i membri staff sono già in rosa."
										: "Tutti i giocatori sono già in rosa."}
							</div>
						)}
					</div>
				</>
			</div>
		</Page>
	);
}

export default HandleRosterPage;
